import type { HotelOffer } from '../api/hotels.types';

/** Share of uppercase letters from which a label is considered written in caps. */
const SHOUTING_RATIO = 0.7;

/** Shorter all-caps words inside mixed text are kept: they are usually acronyms (TV, AC, VIP). */
const MIN_SHOUTED_WORD_LENGTH = 4;

function capitalizeFirst(text: string): string {
  return text.charAt(0).toLocaleUpperCase('es') + text.slice(1);
}

function isUpperCaseWord(word: string): boolean {
  const letters = word.replace(/[^\p{L}]/gu, '');
  return letters.length >= MIN_SHOUTED_WORD_LENGTH && letters === letters.toLocaleUpperCase('es');
}

function isShouting(text: string): boolean {
  const letters = text.replace(/[^\p{L}]/gu, '');
  if (!letters) return false;
  const upper = letters.replace(/[^\p{Lu}]/gu, '').length;
  return upper / letters.length >= SHOUTING_RATIO;
}

function formatSegment(segment: string): string {
  if (isShouting(segment)) return capitalizeFirst(segment.toLocaleLowerCase('es'));

  const words = segment.split(' ');
  if (!words.some(isUpperCaseWord)) return segment;

  return capitalizeFirst(
    words.map((word) => (isUpperCaseWord(word) ? word.toLocaleLowerCase('es') : word)).join(' '),
  );
}

/**
 * Providers send room and board names with arbitrary casing ("DOBLE CON CUARTO DE BAÑO",
 * "Sólo Habitación", "DOBLE Accesible"). Each " - " separated part is handled on its own,
 * since providers often append a mixed-case tail ("HABITACIÓN SUPERIOR - Tarifa Empaquetada"):
 * parts written in caps become sentence case, in mixed parts only long all-caps words are
 * lowered (acronyms survive), and parts already in mixed case stay as the provider wrote them.
 */
export function formatProviderLabel(value: string | undefined): string | undefined {
  const text = value?.trim().replace(/\s+/g, ' ');
  if (!text) return undefined;

  return text.split(' - ').map(formatSegment).join(' - ');
}

/** Same offer with its room and board names normalized for display. */
export function formatOfferLabels(offer: HotelOffer): HotelOffer {
  return {
    ...offer,
    rooms: (offer.rooms ?? []).map((room) => ({
      ...room,
      type: formatProviderLabel(room.type),
      rates: (room.rates ?? []).map((rate) => ({
        ...rate,
        name: formatProviderLabel(rate.name),
        meal: formatProviderLabel(rate.meal),
        board: formatProviderLabel(rate.board),
      })),
    })),
  };
}
